import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import classNames from 'classnames'

import MultiLevelSelect from '../../../../../../../../../../../components/MultiLevelSelect'
import SubAllocationMultiFieldItem from '../SubAllocationTables/SubAllocationMultiFieldItem'

import { flattenAdUnit } from './helpers'

import { AD_UNITS, IS_SUB_ALLOCATION } from '../../../fields'
import {
  getGoogleAdManagerAdUnitSelector,
  getGoogleAdManagerAdUnitWasLoadedSelector,
  getOnsiteRatesSelector
} from '../../../../../../../../../../../modules/selectors/googleAdManager'
import { CPC, CPM, VIEWABLE_CPM } from '../SubAllocationTables/fields'

import useStyles from './styles'

const AdUnitsMultilevelSelect = ({ formik, topParentAdUnitId }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { values, setFieldValue } = formik

  const rootAdUnit = useSelector(getGoogleAdManagerAdUnitSelector)

  const getGoogleAdManagerAdUnitWasLoaded = useSelector(getGoogleAdManagerAdUnitWasLoadedSelector)
  const onsiteRates = useSelector(getOnsiteRatesSelector)

  const formattedFlatAdUnits = useMemo(() => {
    // Flatten each top-level ad unit recursively.
    return rootAdUnit.children?.flatMap(unit => flattenAdUnit(unit))
  }, [rootAdUnit])

  const initiallyExpandedOptionsIds = useMemo(() => {
    // top level children
    const topLevelChildren = formattedFlatAdUnits?.filter(unit => !unit.parent)
    // if there's only one top level child, expand it by default
    return topLevelChildren?.length === 1 ? topLevelChildren.map(item => item.id) : []
  }, [formattedFlatAdUnits])

  const findPriceForAdUnit = useCallback(
    (adUnit, adUnitsMap, billingEventType) => {
      // REQUIREMENTS: Sometimes ad units won't have a price in /onsite_rates/.
      // If an ad unit doesn't have a price then you need to search for the parent's price.
      // If that doesn't have one then you need to search for the price of the grandparent ad unit and so on.
      // The parent_path or parent_id fields at /ad-units/ endpoint can be used to find the parent and grandparent ad unit names and ids.
      if (!onsiteRates || !Array.isArray(onsiteRates)) {
        return 0
      }

      // Check if the ad unit has a price in onsite rates
      const adUnitRate = onsiteRates.find(rate => rate.inventory_id === adUnit.id)
      if (adUnitRate?.prices?.[billingEventType] !== undefined) {
        return adUnitRate.prices[billingEventType]
      }

      // Helper function to find price by traversing parent hierarchy
      const findPriceInParentHierarchy = parentId => {
        if (!parentId) return null

        const parentRate = onsiteRates.find(rate => rate.inventory_id === parentId)
        if (parentRate?.prices?.[billingEventType] !== undefined) {
          return parentRate.prices[billingEventType]
        }

        // Move up to the next parent
        const parentUnit = adUnitsMap[parentId]
        const nextParentId = parentUnit ? parentUnit.parent : null

        return nextParentId ? findPriceInParentHierarchy(nextParentId) : null
      }

      // Try to find price in parent hierarchy
      const parentPrice = findPriceInParentHierarchy(adUnit?.parent)
      if (parentPrice !== null) {
        return parentPrice
      }

      // Check if the top parent ad unit has a price
      if (topParentAdUnitId) {
        const topParentAdUnitRate = onsiteRates.find(rate => rate.inventory_id === topParentAdUnitId)

        if (topParentAdUnitRate?.prices?.[billingEventType] !== undefined) {
          return topParentAdUnitRate.prices[billingEventType]
        }
      }

      // Default to 0 if no price is found
      return 0
    },
    [onsiteRates, topParentAdUnitId]
  )

  const selectAdUnitsHandler = useCallback(
    selectedValues => {
      if (!selectedValues || !Array.isArray(selectedValues)) {
        setFieldValue(AD_UNITS, [])
        return
      }

      // Create a map of ad units for quick lookup
      const adUnitsMap = {}
      if (formattedFlatAdUnits && Array.isArray(formattedFlatAdUnits)) {
        formattedFlatAdUnits.forEach(unit => {
          if (unit && unit.id) {
            adUnitsMap[unit.id] = unit
          }
        })
      }

      const selectedValuesWithPreselectedPrices = selectedValues.map(adUnit => {
        return {
          ...adUnit,
          [CPM]: findPriceForAdUnit(adUnit, adUnitsMap, CPM),
          [CPC]: findPriceForAdUnit(adUnit, adUnitsMap, CPC),
          [VIEWABLE_CPM]: findPriceForAdUnit(adUnit, adUnitsMap, VIEWABLE_CPM)
        }
      })

      setFieldValue(AD_UNITS, [...values[AD_UNITS], ...selectedValuesWithPreselectedPrices])
    },
    [values, setFieldValue, findPriceForAdUnit, formattedFlatAdUnits]
  )

  return (
    <MultiLevelSelect
      placeholder={t('Add ad unit')}
      setFieldValue={setFieldValue}
      value={values[AD_UNITS]}
      name={AD_UNITS}
      options={formattedFlatAdUnits}
      isLoading={!getGoogleAdManagerAdUnitWasLoaded}
      CustomMultiFieldItem={values[IS_SUB_ALLOCATION] ? <SubAllocationMultiFieldItem formik={formik} /> : null}
      setFullOptionObject={true}
      onValuesWithFullObjectSelectCallback={selectAdUnitsHandler}
      shouldSelectParentOnAllChildrenSelected={false}
      className={classNames({
        [classes.resetMarginTop]: values[IS_SUB_ALLOCATION]
      })}
      selectedOptionsWrapperClassName={classNames({
        [classes.selectedOptionsWrapper]: values[IS_SUB_ALLOCATION]
      })}
      initiallyExpandedOptionsIds={initiallyExpandedOptionsIds}
    />
  )
}

export default AdUnitsMultilevelSelect
